import CircularProgress from '@mui/material/CircularProgress';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide, SwiperClass } from '../Swiper';
import { favorite } from '../../api/index';
import { openSigninOn401Error } from '../../helpers/asyncActions';
import { getImageUrl, getPropertyImages } from '../../helpers/images';
import { useGlobalContext } from '../../stores/global';
import styles from './Property.module.scss';
import { getFormattedPrice } from '../../helpers/price';
import { getSingularPluralWord } from '../../i18n/translations';
import { isPhone } from '../../helpers/browser';
import { Tooltip } from '@mui/material';

interface Props {
  property: any;
  onFavorite?: (isFavorite: boolean) => void;
  type?: 'mapinfo';
  onTouchStart?: (
    swiper: SwiperClass,
    event: MouseEvent | TouchEvent | PointerEvent
  ) => void | undefined;
}

export function Property({ property, onFavorite, type, onTouchStart }: Props) {
  const [slide, setSlide] = useState(0);
  const [isFavorite, setFavorite] = useState(false);
  const { dispatch } = useGlobalContext();

  useEffect(() => {
    setFavorite(property.favorite);
  }, []);

  const handleFavorite = () => {
    favorite(property._id)
      .then((response) => {
        const newIsFavorite = !!response.data.data.added;
        setFavorite(newIsFavorite);
        onFavorite?.(newIsFavorite);
      })
      .catch(openSigninOn401Error.bind(null, dispatch));
  };

  if (!property?.id) return null;

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const goToLink = () => {
    window.open(`/properties/${property.id}`, '_blank');
  };

  return (
    <div
      className={
        styles.container + (type === 'mapinfo' ? ` ${styles.mapInfo}` : '')
      }
      onClick={() => goToLink()}
    >
      <span className={styles.swiperContainer}>
        <Swiper
          className={styles.swiper}
          tag="section"
          onTouchStart={onTouchStart}
          wrapperTag="ul"
          onSlideChange={(e: any) => setSlide(e.activeIndex)}
          pagination={{ clickable: true, dynamicBullets: true }}
          navigation={!isPhone}
        >
          {getPropertyImages(property.attaches).map((attach: any, index) => (
            <SwiperSlide key={index} tag="li">
              {index <= slide + 1 ? (
                <span className={styles.loadingImage}>
                  <CircularProgress />
                  <a
                    target="_blank"
                    href={`/properties/${property.id}`}
                    style={{
                      backgroundImage: `url(${getImageUrl(attach, {
                        urn: '/info/thumbnail',
                      })})`,
                    }}
                    className={styles.loadingImageImg}
                  />
                </span>
              ) : (
                ''
              )}
            </SwiperSlide>
          ))}
        </Swiper>
        <a className={styles.favorite} onClick={() => handleFavorite()}>
          {isFavorite ? (
            <i className="fonticons fonticons-heart-filled" />
          ) : (
            <i className="fonticons fonticons-heart" />
          )}
        </a>
      </span>
      <span className={clsx(styles.info, styles.title)}>
        <Tooltip title={property.description}>
          <span className={clsx(styles.shortText)}>{property.description}</span>
        </Tooltip>
      </span>
      <span className={styles.info2}>
        <i className="fonticons fonticons-pin" /> &nbsp;
        <span className={clsx(styles.address)}>
          {property.address.city ? `${property.address.city} | ` : ''}
          {property.address.neighbor || property.address.name}
        </span>
      </span>
      <span className={clsx(styles.info, styles.subtitle)}>
        {getFormattedPrice(property.prices.property)}
      </span>
      <span className={styles.box}>
        {property.info.rooms}{' '}
        <span style={{ textTransform: 'capitalize' }}>
          {getSingularPluralWord('room', property.info.rooms)}
        </span>
        {' | '}
        {property.info.toilets}{' '}
        <span style={{ textTransform: 'capitalize' }}>
          {getSingularPluralWord('bathroom', property.info.toilets)}
        </span>
        {' | '}
        {property.info.squareMeters} m²
      </span>
      <span className={clsx(styles.info)}>
        <span className={clsx(styles.address)}>
          {capitalizeFirstLetter(property.info?.type)}
        </span>
      </span>
    </div>
  );
}
